/**
 * ContactCard - Unified contact display component
 *
 * Normalizes contact data from different Filevine schemas and renders
 * a consistent display. Uses adapter functions to convert from:
 * - ProjectClientSchema (Filevine snapshot data)
 * - FilevineContactWithHandlesSchema (API-fetched contact data)
 */

import { Building2, Mail, MapPin, Phone, User } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import type {
  FilevineContactAddressSchema,
  FilevineContactWithHandlesSchema,
} from '@/services/api/filevineResourcesService';
import type { ProjectClientSchema } from '@/services/api/generated/entitySyncService';

// Extract address type from ProjectClientSchema
type SnapshotAddressSchema = NonNullable<
  ProjectClientSchema['addresses']
>[number];

// ============================================================================
// Types
// ============================================================================

/** Normalized contact data for display */
export interface ContactCardData {
  fullName: string;
  isCompany?: boolean;
  primaryPhone?: string;
  primaryEmail?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

export interface ContactCardProps {
  /** Normalized contact data */
  contact: ContactCardData;
  /** Label shown above the name (e.g., "Provider") */
  label?: string;
  /** 'card' = prominent card style, 'inline' = compact inline style */
  variant?: 'card' | 'inline';
  className?: string;
}

// ============================================================================
// Adapter Functions
// ============================================================================

/**
 * Convert ProjectClientSchema (Filevine snapshot) to normalized form.
 *
 * Snapshot schema uses:
 * - fullname (not full_name)
 * - phones[].number (not phone_number)
 * - emails[].address (not email)
 * - addresses[].address_line1, province_or_state, postal_or_zip
 */
export function fromProjectClientSchema(
  person: ProjectClientSchema,
): ContactCardData {
  const addr = person.addresses?.[0];
  return {
    fullName: person.fullname,
    isCompany: Boolean(person.fromCompany),
    primaryPhone: person.phones?.[0]?.number,
    primaryEmail: person.emails?.[0]?.address,
    address: addr ? normalizeSnapshotAddress(addr) : undefined,
  };
}

/**
 * Convert FilevineContactWithHandlesSchema (API contact) to normalized form.
 *
 * API schema uses:
 * - full_name (not fullname)
 * - phones[].phone_number (not number)
 * - emails[].email (not address)
 * - addresses[].line1, state, zip
 */
export function fromFilevineContactSchema(
  contact: FilevineContactWithHandlesSchema,
): ContactCardData {
  const addr = contact.addresses?.[0];
  return {
    fullName: contact.full_name,
    isCompany: false, // API doesn't expose this field
    primaryPhone: contact.phones?.[0]?.phone_number,
    primaryEmail: contact.emails?.[0]?.email,
    address: addr ? normalizeApiAddress(addr) : undefined,
  };
}

// ============================================================================
// Address Normalization Helpers
// ============================================================================

function normalizeSnapshotAddress(
  addr: SnapshotAddressSchema,
): ContactCardData['address'] {
  return {
    line1: addr.address_line1 ?? undefined,
    line2: addr.address_line2 ?? undefined,
    city: addr.city ?? undefined,
    state: addr.province_or_state ?? undefined,
    zip: addr.postal_or_zip ?? undefined,
  };
}

function normalizeApiAddress(
  addr: FilevineContactAddressSchema,
): ContactCardData['address'] {
  return {
    line1: addr.line1 ?? undefined,
    line2: addr.line2 ?? undefined,
    city: addr.city ?? undefined,
    state: addr.state ?? undefined,
    zip: addr.zip ?? undefined,
  };
}

// ============================================================================
// Display Helpers
// ============================================================================

function formatAddress(address: ContactCardData['address']): string | null {
  if (!address) return null;

  const cityStateZip = [address.city, address.state, address.zip]
    .filter(Boolean)
    .join(', ');

  const parts = [address.line1, address.line2, cityStateZip].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Icon for the contact name - person or company */
const NameIcon: React.FC<{ isCompany?: boolean; className?: string }> = ({
  isCompany,
  className,
}) =>
  isCompany ? (
    <Building2 className={className} />
  ) : (
    <User className={className} />
  );

/** Contact detail row (phone, email, address) */
const DetailRow: React.FC<{
  icon: React.ReactNode;
  value: string;
  className?: string;
}> = ({ icon, value, className }) => (
  <div className={cn('flex items-center gap-1.5', className)}>
    {icon}
    <span>{value}</span>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * ContactCard - Displays normalized contact information
 *
 * Supports two variants:
 * - 'card': Prominent display with background and padding (for ProviderCard use)
 * - 'inline': Compact inline display (for LinkedContactPreview use)
 */
export const ContactCard: React.FC<ContactCardProps> = ({
  contact,
  label,
  variant = 'inline',
  className,
}) => {
  const formattedAddress = formatAddress(contact.address);

  if (variant === 'card') {
    return (
      <div
        className={cn(
          'rounded-lg border bg-zinc-50/50 p-3 space-y-2',
          className,
        )}
      >
        {/* Label if provided */}
        {label && (
          <div className="text-xs font-medium text-zinc-500">{label}</div>
        )}

        {/* Name */}
        <div className="flex items-center gap-2 font-medium text-zinc-900">
          <NameIcon
            isCompany={contact.isCompany}
            className="h-4 w-4 text-zinc-500"
          />
          {contact.fullName}
        </div>

        {/* Contact details */}
        <div className="space-y-1 pl-6 text-sm text-zinc-600">
          {contact.primaryPhone && (
            <DetailRow
              icon={<Phone className="h-3.5 w-3.5 text-zinc-400" />}
              value={contact.primaryPhone}
            />
          )}
          {contact.primaryEmail && (
            <DetailRow
              icon={<Mail className="h-3.5 w-3.5 text-zinc-400" />}
              value={contact.primaryEmail}
            />
          )}
          {formattedAddress && (
            <DetailRow
              icon={<MapPin className="h-3.5 w-3.5 text-zinc-400" />}
              value={formattedAddress}
            />
          )}
        </div>
      </div>
    );
  }

  // Inline variant
  return (
    <div className={cn('space-y-1', className)}>
      {/* Name */}
      <div className="flex items-center gap-1.5 font-medium text-zinc-900">
        <NameIcon
          isCompany={contact.isCompany}
          className="h-3.5 w-3.5 text-zinc-500"
        />
        {contact.fullName}
      </div>

      {/* Contact details - inline style */}
      {contact.primaryPhone && (
        <div className="flex items-center gap-1.5 pl-5 text-xs text-zinc-500">
          <Phone className="h-3 w-3" />
          {contact.primaryPhone}
        </div>
      )}
      {contact.primaryEmail && (
        <div className="flex items-center gap-1.5 pl-5 text-xs text-zinc-500">
          <Mail className="h-3 w-3" />
          {contact.primaryEmail}
        </div>
      )}
      {formattedAddress && (
        <div className="flex items-start gap-1.5 pl-5 text-xs text-zinc-500">
          <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0" />
          <span>{formattedAddress}</span>
        </div>
      )}
    </div>
  );
};

export default ContactCard;
